import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import uniq from 'lodash/uniq';
import { NormalizedSchema } from 'normalizr';
import { FetchStatus, SavePayloadAction, SchemaKeys } from '../core';
import { HomeEntities, HomeItems } from './schema';

interface HomeState {
  items: HomeItems;
  itemsFetchStatus: FetchStatus;
}

const initialState: HomeState = {
  items: {
    myTasks: [],
    myCompanyTasks: [],
  },
  itemsFetchStatus: FetchStatus.None,
};

const homeSlice = createSlice({
  name: SchemaKeys.Home,
  initialState,

  reducers: (create) => ({
    saveHomeResponse: create.reducer(
      (
        state: HomeState,
        {
          payload: { data, fetchStatus },
        }: SavePayloadAction<NormalizedSchema<HomeEntities, HomeItems>>,
      ) => {
        return {
          ...state,
          items: { ...data.result },
          itemsFetchStatus: fetchStatus,
        };
      },
    ),

    saveCompanyTasks: create.reducer(
      (state: HomeState, { payload }: PayloadAction<number[]>): HomeState => {
        const myCompanyTasks = uniq([
          ...(state.items.myCompanyTasks ?? []),
          ...payload,
        ]);

        return {
          ...state,
          items: {
            ...state.items,
            myCompanyTasks,
          },
        };
      },
    ),

    fetchHomeResponseError: create.reducer((state: HomeState): HomeState => {
      return {
        ...state,
        itemsFetchStatus: FetchStatus.Loaded,
      };
    }),

    removeTask: create.reducer(
      (state: HomeState, { payload: taskId }: PayloadAction<number>) => {
        return {
          ...state,
          items: {
            ...state.items,
            myTasks: state.items.myTasks.filter((t) => t !== taskId),
            myCompanyTasks: (state.items.myCompanyTasks ?? []).filter(
              (t) => t !== taskId,
            ),
          },
        };
      },
    ),
  }),
});

export const homeReducer = homeSlice.reducer;
export const homeStateName = homeSlice.name;
export const {
  saveHomeResponse,
  saveCompanyTasks,
  fetchHomeResponseError,
  removeTask,
} = homeSlice.actions;
